import { ArrowRight, BadgeCheck, CalendarDays, FileSignature, MonitorPlay, Phone, UserPlus, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../shared/format'
import type { CrmOverview } from '../types'
import { useCrm } from '../ui'

const FUNNEL = {
  lead: { icon: UserPlus, chip: 'bg-violet/12 text-violet', bar: 'bg-violet' },
  demo: { icon: MonitorPlay, chip: 'bg-info/12 text-info', bar: 'bg-info' },
  proposal: { icon: FileSignature, chip: 'bg-brand-orange/12 text-brand-orange', bar: 'bg-brand-orange' },
  customer: { icon: BadgeCheck, chip: 'bg-success/12 text-success', bar: 'bg-success' },
} as const

export function FunnelCard({ overview, title }: { overview?: CrmOverview; title: string }) {
  const { t } = useTranslation()
  return (
    <section className="rounded-xl border bg-card shadow-card">
      <h2 className="border-b px-4 py-3 font-semibold">{title}</h2>
      {!overview ? (
        <Skeleton className="m-4 h-40" />
      ) : (
        <ul className="space-y-1 p-2">
          {overview.funnel.map((f) => {
            const { icon: Icon, chip, bar } = FUNNEL[f.stage]
            return (
              <li key={f.stage} className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50">
                <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-xl', chip)}>
                  <Icon className="size-5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium">{t(`crm.stage.${f.stage}`)}</span>
                    <span className="shrink-0 tabular-nums">
                      <span className="text-lg font-semibold">{f.count}</span>
                      <span className="ml-1.5 text-xs text-muted-foreground">%{f.pct}</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted" role="meter" aria-valuenow={f.pct} aria-valuemin={0} aria-valuemax={100} aria-label={t(`crm.stage.${f.stage}`)}>
                    <div className={cn('h-full rounded-full transition-[width]', bar)} style={{ width: `${f.pct}%` }} />
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

const FOLLOW_ICON = { call: Phone, meeting: CalendarDays, collection: Wallet }

export function FollowUpsCard({ overview, limit = 6 }: { overview?: CrmOverview; limit?: number }) {
  const { t } = useTranslation()
  const { customerName } = useCrm()
  const items = overview?.followUps ?? []
  return (
    <section className="rounded-xl border bg-card shadow-card">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <h2 className="flex-1 font-semibold">{t('crm.followUps.title')}</h2>
        <Badge tone="danger">{items.length}</Badge>
      </header>
      {!overview ? (
        <Skeleton className="m-4 h-40" />
      ) : items.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">{t('crm.followUps.empty')}</p>
      ) : (
        <ul className="divide-y">
          {items.slice(0, limit).map((f) => {
            const Icon = FOLLOW_ICON[f.kind]
            const late = f.overdueDays > 0
            return (
              <li key={`${f.customerId}-${f.kind}-${f.date}`}>
                <Link to={`/customers/${f.customerId}`} className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/40">
                  <Icon className={cn('mt-0.5 size-4 shrink-0', late ? 'text-destructive' : 'text-primary')} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{customerName(f.customerId)}</span>
                    <span className="block truncate text-xs text-muted-foreground">{f.kind === 'collection' ? t('crm.followUps.collection', { code: f.text }) : f.text}</span>
                  </span>
                  <span className="shrink-0 text-right text-xs">
                    <span className={cn('block tabular-nums', late && 'text-destructive')}>{fmtDate(f.date)}</span>
                    <span className={late ? 'text-destructive' : 'text-primary'}>{late ? t('crm.followUps.late', { count: f.overdueDays }) : t('crm.followUps.upcoming')}</span>
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
      <Link to="/customers/meetings" className="flex items-center justify-center gap-1 border-t py-2.5 text-sm font-medium text-primary hover:underline">
        {t('crm.followUps.all')}
        <ArrowRight className="size-4" aria-hidden />
      </Link>
    </section>
  )
}
